import { Fragment, useEffect, useMemo } from 'react'

import type { AppProps } from 'next/app'
import { useRouter } from 'next/router'
import { appWithTranslation } from 'next-i18next'
import type { PropsWithChildren } from 'react'
import { QueryClient, QueryClientProvider } from 'react-query'
import { BrowserRouter as Router } from 'react-router-dom'

import { Provider as CashDesignSystemProvider } from '@cash-design-system/react'
import { FaviconTags } from '@cash-web/favicon-next'
import { Country } from '@cash-web/protos/squareup/common/countries.pb'
import logger from '@cash-web/shared/util-logger'
import { ExperimentsProvider } from '@cash-web/shared-data-access-experiments'
import { FeatureFlagProvider } from '@cash-web/shared-data-access-feature-flags'
import type { Region } from '@cash-web/shared-data-access-region-provider'
import { RegionProvider } from '@cash-web/shared-data-access-region-provider'
import { JourneyProvider } from '@cash-web/shared-journey-feature-main'
import { journeyRoutes } from '@cash-web/shared-journey-feature-routes'
import { isServerEnvironment } from '@cash-web/shared-util-environment'
import { initializeRum } from '@cash-web/shared-util-rum'
import { AnalyticsProvider } from '@cash-web/support/utils'

import Layout from '../Layout'
import i18nextConfig from '../next-i18next.config'
import { useDarkMode } from '../src/hooks/useDarkMode'
import { start } from '../src/lib/bugsnag'
import { getClient } from '../src/lib/launchDarkly'
import type { DatadogConfig } from '../src/lib/launchDarklyFlags'
import { flags } from '../src/lib/launchDarklyFlags'
import { FeatureFlagProvider as HelpFeatureFlagProvider } from '../src/providers/FeatureFlagProvider'

const queryClient = new QueryClient()

start()

const getRegionFromQuery = (query: { region?: string }) => {
  const lookupRegion = query.region?.toString().toUpperCase()
  return lookupRegion && Object.keys(Country).includes(lookupRegion) ? Country[lookupRegion as Country] : Country.US
}

const RumInitializer = () => {
  const router = useRouter()

  useEffect(() => {
    if (typeof window !== 'undefined') {
      try {
        const client = getClient()
        if (client) {
          client
            .loadFlags()
            .then(() => {
              try {
                const rumFeatureFlagEnabled = client.getFlagValue(flags['rumFeatureFlagEnabled']) ?? false
                const datadogConfig = client.getFlagValue(flags['datadogConfig']) as DatadogConfig | undefined

                if (rumFeatureFlagEnabled) {
                  const region = getRegionFromQuery(router.query)
                  window.countryGuess = region
                  initializeRum(datadogConfig?.sessionSampleRateOverride || 0)
                }
              } catch (e) {
                logger.error(new Error(`error processing RUM flags for help-center: ${e}`))
              }
            })
            .catch(e => {
              logger.error(new Error(`error loading feature flags for help-center: ${e}`))
            })
        }
      } catch (e) {
        logger.error(new Error(`error initializing RUM for help-center: ${e}`))
      }
    }
  }, [router.query])

  return null
}

const ThemeProviderWrapper = ({ children }: PropsWithChildren) => {
  const [isDarkMode, mounted] = useDarkMode()

  if (!mounted) return null

  return (
    <CashDesignSystemProvider themeVariant="monochrome" darkMode={isDarkMode}>
      {children}
    </CashDesignSystemProvider>
  )
}

const HelpCenterApp = ({ Component, pageProps }: AppProps) => {
  const router = useRouter()
  const region = getRegionFromQuery(router.query)

  // Ensure modal-root element exists on client side
  useEffect(() => {
    if (typeof window !== 'undefined' && !document.getElementById('modal-root')) {
      const modalRoot = document.createElement('div')
      modalRoot.id = 'modal-root'
      document.body.appendChild(modalRoot)
    }
  }, [])

  const ErrorBoundary = useMemo(() => {
    if (isServerEnvironment()) {
      return Fragment
    }
    return logger.createErrorBoundary() || Fragment
  }, [])

  return (
    <>
      <FaviconTags />
      <RumInitializer />
      <ErrorBoundary>
        <ExperimentsProvider>
          <QueryClientProvider client={queryClient}>
            <RegionProvider region={region as Region}>
              <AnalyticsProvider>
                <HelpFeatureFlagProvider>
                  <FeatureFlagProvider>
                    <ThemeProviderWrapper>
                      <Layout>
                        <Router>
                          <JourneyProvider routes={journeyRoutes}>
                            <Component {...pageProps} />
                          </JourneyProvider>
                        </Router>
                      </Layout>
                    </ThemeProviderWrapper>
                  </FeatureFlagProvider>
                </HelpFeatureFlagProvider>
              </AnalyticsProvider>
            </RegionProvider>
          </QueryClientProvider>
        </ExperimentsProvider>
      </ErrorBoundary>
    </>
  )
}

export default appWithTranslation(HelpCenterApp, i18nextConfig)
